import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase, API_URL } from '../utils/constants';

const ProfilePage = ({ session, onBack, showToast, onProfileUpdated }) => {
  const { t } = useTranslation();
  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');
  const [preferredStyle, setPreferredStyle] = useState('thread');
  const [subscriptionTier, setSubscriptionTier] = useState('free');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [managingSubscription, setManagingSubscription] = useState(false);

  const loadProfile = useCallback(async () => {
    try {
      const { data } = await supabase
        .from('profiles')
        .select('display_name, bio, preferred_style, subscription_tier')
        .eq('id', session.user.id)
        .single();

      if (data) {
        setDisplayName(data.display_name || '');
        setBio(data.bio || '');
        setPreferredStyle(data.preferred_style || 'thread');
        setSubscriptionTier(data.subscription_tier || 'free');
      }
    } catch (err) {
      console.error('Failed to load profile:', err);
    } finally {
      setLoading(false);
    }
  }, [session.user.id]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const saveProfile = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .upsert({
          id: session.user.id,
          display_name: displayName,
          bio: bio,
          preferred_style: preferredStyle,
          updated_at: new Date().toISOString()
        });

      if (error) throw error;
      onProfileUpdated?.({
        display_name: displayName,
        bio,
        preferred_style: preferredStyle
      });
      showToast(t('profile.profileSaved'), 'success');
    } catch (err) {
      showToast(t('profile.profileSaveFailed') + err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '40px', height: '40px', border: '3px solid var(--border-color)', borderTopColor: 'var(--accent-primary)', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
          <p style={{ color: 'var(--text-secondary)' }}>{t('profile.loadingProfile')}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-primary)', padding: '40px 20px' }}>
      <div style={{ maxWidth: '600px', margin: '0 auto' }}>
        <button
          onClick={onBack}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px',
            background: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
            borderRadius: '10px', color: 'var(--text-secondary)', fontSize: '14px',
            cursor: 'pointer', fontFamily: 'inherit', marginBottom: '32px'
          }}
        >
          {t('profile.backToApp')}
        </button>

        <div style={{ background: 'var(--bg-card)', borderRadius: '20px', padding: '40px', boxShadow: 'var(--shadow)', border: '1px solid var(--border-color)' }}>
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <div style={{
              width: '80px', height: '80px', borderRadius: '50%',
              background: 'var(--accent-gradient)', display: 'flex',
              alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
              fontSize: '32px', color: 'white', fontWeight: '700'
            }}>
              {displayName?.[0]?.toUpperCase() || session.user.email?.[0]?.toUpperCase() || '?'}
            </div>
            <h1 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '4px' }}>{t('profile.title')}</h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>{session.user.email}</p>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '8px' }}>
              {t('profile.displayName')}
            </label>
            <input
              type="text"
              placeholder={t('profile.displayNamePlaceholder')}
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              style={{
                width: '100%', padding: '14px 16px', borderRadius: '10px',
                border: '2px solid var(--border-color)', background: 'var(--bg-secondary)',
                color: 'var(--text-primary)', fontSize: '15px', outline: 'none',
                fontFamily: 'inherit', boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '8px' }}>
              {t('profile.bio')}
            </label>
            <textarea
              placeholder={t('profile.bioPlaceholder')}
              value={bio}
              onChange={e => setBio(e.target.value)}
              rows={3}
              style={{
                width: '100%', padding: '14px 16px', borderRadius: '10px',
                border: '2px solid var(--border-color)', background: 'var(--bg-secondary)',
                color: 'var(--text-primary)', fontSize: '15px', outline: 'none',
                fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical'
              }}
            />
          </div>

          <div style={{ marginBottom: '32px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '12px' }}>
              {t('profile.preferredStyle')}
            </label>
            <div style={{ display: 'flex', gap: '12px' }}>
              {[
                { id: 'thread', label: '🧵 ' + t('profile.threads'), desc: t('profile.threadsDesc') },
                { id: 'article', label: '📝 ' + t('profile.articles'), desc: t('profile.articlesDesc') }
              ].map(option => (
                <button
                  key={option.id}
                  onClick={() => setPreferredStyle(option.id)}
                  style={{
                    flex: 1, padding: '16px', borderRadius: '12px',
                    border: `2px solid ${preferredStyle === option.id ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                    background: preferredStyle === option.id ? 'var(--accent-subtle-bg)' : 'var(--bg-secondary)',
                    cursor: 'pointer', textAlign: 'center', fontFamily: 'inherit'
                  }}
                >
                  <div style={{ fontSize: '20px', marginBottom: '8px' }}>{option.label}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{option.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={saveProfile}
            disabled={saving}
            style={{
              width: '100%', padding: '16px', borderRadius: '12px', border: 'none',
              background: 'var(--accent-gradient)', color: 'white',
              fontSize: '16px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
              opacity: saving ? 0.7 : 1, transition: 'opacity 0.2s'
            }}
          >
            {saving ? t('profile.saving') : t('profile.saveChanges')}
          </button>

          {subscriptionTier === 'pro' && (
            <button
              onClick={async () => {
                setManagingSubscription(true);
                try {
                  const res = await fetch(`${API_URL}/api/create-portal-session`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' }
                  });
                  const data = await res.json();
                  if (data.url) window.location.href = data.url;
                  else showToast('Could not open subscription portal', 'error');
                } catch {
                  showToast('Could not open subscription portal', 'error');
                } finally {
                  setManagingSubscription(false);
                }
              }}
              disabled={managingSubscription}
              style={{
                width: '100%', padding: '16px', borderRadius: '12px',
                border: '2px solid var(--border-color)', background: 'var(--bg-secondary)',
                color: 'var(--text-primary)', fontSize: '16px', fontWeight: '600',
                cursor: 'pointer', fontFamily: 'inherit', marginTop: '12px',
                opacity: managingSubscription ? 0.7 : 1, transition: 'opacity 0.2s'
              }}
            >
              {managingSubscription ? 'Opening...' : 'Manage Subscription'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
